import React, { useEffect, useState } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyList from "go-core/components/EmptyList";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { ReactComponent as WarningAlertSVG } from "go-core/images/svg/warning-alert.svg";
import FormatResourceStatus from "../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { TerminalApi } from "../../../../../../../services/Api/Admin/adminTypes";
import { getTerminalVersionStatusAlert } from "../../services/alerts";
import { OrganizationPreviewRequests } from "../../services/types";

interface Props {
	organizationId: number;
	requests: OrganizationPreviewRequests;
}

const TerminalsTabPreview = ({ organizationId, requests }: Props) => {
	const { t } = useTranslation("admin");
	const { addFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(true);
	const [terminals, setTerminals] = useState<TerminalApi[]>([]);

	useEffect(() => {
		onFetchTerminals();
	}, []);

	const onFetchTerminals = async () => {
		try {
			const res = await requests.terminals(organizationId);
			setTerminals(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const drawTerminalAlerts = (terminal: TerminalApi) => {
		const alerts = getTerminalVersionStatusAlert(t, terminal.is_outdated, terminal.is_test_version);

		const alertsStatus = alerts?.find((alert) => alert?.type === "DANGER") ? "DANGER" : "WARNING";

		if (alerts?.filter((alert) => alert.title)?.length > 0) {
			return (
				<OverlayTrigger
					placement="top"
					overlay={
						<Tooltip id={`tooltip-terminal-${terminal.id}-alerts`}>
							<div className="d-flex flex-column">
								{alerts.map((alert, index) => (
									<span key={`terminal-${terminal.id}-alert-${index}`}>{alert.title}</span>
								))}
							</div>
						</Tooltip>
					}
				>
					{alertsStatus === "DANGER" ? (
						<AlertSVG className="me-1 alert-svg" />
					) : (
						<WarningAlertSVG className="me-1 alert-svg" />
					)}
				</OverlayTrigger>
			);
		}
	};

	const drawTerminalDetails = (terminal: TerminalApi) => {
		let str = "";
		if (terminal.ip_address) {
			str += `${t("lib:common.word.ip_address")} ${terminal.ip_address}\n`;
		}
		if (terminal.mac_address) {
			str += `${t("lib:common.word.mac_address")} ${terminal.mac_address}`;
		}
		return str;
	};

	if (loading) return <LoadingContainer />;

	return (
		<Card className="terminals-tab-preview-card">
			<Card.Body>
				{terminals.length > 0 ? (
					<div className="table-responsive">
						<table className="table table-preview">
							<thead>
								<tr>
									<th>{t("lib:common.word.name")}</th>
									<th>{t("common.word.app_version")}</th>
									<th>{t("lib:common.word.created_at")}</th>
									<th>{t("common.word.details")}</th>
									<th>{t("lib:common.word.status")}</th>
								</tr>
							</thead>
							<tbody>
								{terminals.map((terminal, index) => (
									<tr key={`terminal-${index}`}>
										<td>
											<div className="d-flex flex-column">
												<span>
													{drawTerminalAlerts(terminal)}
													<Link to={terminal.link}>{terminal.name}</Link>
												</span>
												<small className="text-muted">{terminal.terminal_unique_id}</small>
											</div>
										</td>
										<td>
											<div className="d-flex flex-column">
												<span>{terminal.app_version}</span>
												<span>{terminal.system_type}</span>
												<div>
													<span className="badge bg-secondary">{terminal.type}</span>
												</div>
											</div>
										</td>
										<td>{FormatDate(terminal.created_at)}</td>
										<td>{drawTerminalDetails(terminal)}</td>
										<td>
											<FormatResourceStatus status={terminal.status} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<EmptyList
						title={t("modules.organization.field.no_terminals.title")}
						description={t("modules.organization.field.no_terminals.description")}
					/>
				)}
			</Card.Body>
		</Card>
	);
};

export default TerminalsTabPreview;
